import { Typography } from '@mui/joy';
import { mergeProps, useEffectEvent } from '@react-aria/utils';
import {
  getHours,
  getMinutes,
  getSeconds,
  isAfter,
  isBefore,
  isValid,
  setHours,
  setMinutes,
  setSeconds,
  startOfDay,
  toDate,
} from 'date-fns';
import {
  ComponentPropsWithoutRef,
  forwardRef,
  useEffect,
  useRef,
  useState,
} from 'react';
import { z } from 'zod';

import { useFormController } from './form';
import { Select, Option as SelectOption } from './select';
import { createSplitProps } from './utils';

type TimePickerProps<T = Date> = {
  /** Render options in AM/PM format or 24 hour format. Defaults to 12 hour. */
  format?: '12' | '24';
  /** Callback for when time selection changes. */
  onChange?: (a: T | null) => unknown;
  /** Set to true to allow times that aren't displayed in the options list to be entered manually. Defaults to false. */
  //   creatable?: boolean;
  /** Amount of seconds between each option time. Defaults to 900 (15 minutes). */
  step?: number;
  /**
   * Optional value that can be provided to fully control the component. If not provided, TimePicker
   * will manage state internally and default to the closest step to new Date().
   */
  value?: T | null;
  nullable?: boolean;
  minTime?: T;
  maxTime?: T;
  /**
   * There are two strategies available for setting min/max times. If the min/max datetimes are
   * beyond the start/end times of the current value's date, Timepicker could either clamp the
   * min/max time of the start/end of the day or it can ignore the min/max date components and
   * only apply the time components. Each strategy has the potential to save developer time in
   * avoiding the need to perform conversion before providing min/max datetimes to the component.
   * By default the component will clamp the time window and setting this flag will ignore dates.
   */
  ignoreMinMaxDateComponent?: boolean;
  /**
   * first
   */
  disabledSlot?: Array<number> | ((arg: number) => boolean);
};

type Option = { id: string; label: string };
type TimePickerState = {
  /** List of times (in seconds) displayed in the dropdown menu. */
  steps?: number[];
  /** Internal value of the selected time as an integer since midnight (0) */
  value: Option | undefined | null;
};

const MINUTE = 60;
const HOUR = MINUTE * 60;
const DAY = HOUR * 24;
const NOON = DAY / 2;

// type Value = ReadonlyArray<Option>;
// function filterOptions(excludeOptions?: Value | null) {
//   const filterValue = state.inputValue.trim();
//   // apply filter function
//   if (props.filterOptions) {
//     options = props.filterOptions(
//       options,
//       filterValue,
//       excludeOptions,
//       {
//         // @ts-ignore
//         valueKey: props.valueKey,
//         // @ts-ignore
//         labelKey: props.labelKey,
//       },
//     );
//   }
//   // can user create a new option + there's no exact match already
//   const filterDoesNotMatchOption = props.ignoreCase
//     ? // @ts-ignore
//       (opt) =>
//         opt[props.labelKey].toLowerCase() !==
//         filterValue.toLowerCase().trim()
//     : // @ts-ignore
//       (opt) => opt[props.labelKey] !== filterValue.trim();
//   if (
//     filterValue &&
//     props.creatable &&
//     // @ts-ignore
//     options.concat(props.value).every(filterDoesNotMatchOption)
//   ) {
//     // @ts-expect-error todo(flow->ts) options is typed as a read-only array
//     options.push({
//       id: filterValue,
//       // @ts-ignore
//       [props.labelKey]: filterValue,
//       // @ts-ignore
//       [props.valueKey]: filterValue,
//       isCreatable: true,
//     });
//   }
//   return options;
// }

export const dateToSeconds: <T extends Date>(a: T) => number = <T extends Date>(
  date: T,
) => {
  const seconds = getSeconds(date);
  const minutes = getMinutes(date) * MINUTE;
  const hours = getHours(date) * HOUR;
  return seconds + minutes + hours;
};

const toSafeDate = <
  TArg = undefined,
  TRes = TArg extends null ? null : TArg extends undefined ? Date : Date | null,
>(
  value?: TArg,
): TRes => {
  if (typeof value === 'undefined') {
    return new Date() as TRes;
  }

  if (value === null) {
    return null as TRes;
  }

  return new Date(value as string | number) as TRes;
};

const secondsToHourMinute: (a: number) => [number, number] = (seconds) => {
  const d = toDate(toSafeDate(seconds * 1000));
  return [d.getUTCHours(), d.getUTCMinutes()];
};

const secondsToLabel = (seconds: number, format?: '12' | '24') => {
  const dateFromSeconds = toDate(seconds * 1000);
  let hours = dateFromSeconds.getUTCHours();
  const minutes = dateFromSeconds.getUTCMinutes();

  const zeroPrefix = (n: number) => (n < 10 ? `0${n}` : n);

  if (format === '12') {
    const isAfterNoon = seconds >= NOON;
    if (isAfterNoon) {
      hours -= 12;
    }

    if (hours === 0) {
      hours = 12;
    }

    return `${hours}:${zeroPrefix(minutes)} ${isAfterNoon ? 'PM' : 'AM'}`;
  }

  return `${zeroPrefix(hours)}:${zeroPrefix(minutes)}`;
};

const buildSelectedOption = <T extends Date>(
  value: T,
  format: '12' | undefined | null | '24' = '12',
): Option => {
  const seconds = getSeconds(value);
  const minutes = getMinutes(value) * MINUTE;
  const hours = getHours(value) * HOUR;

  const secs = seconds + minutes + hours;
  return {
    id: String(secs),
    label: secondsToLabel(secs, format || '12'),
  };
};

const setTime = <T extends Date>(
  val: T | undefined | null,
  hours: number,
  minutes: number,
  seconds: number,
) => {
  const date = startOfDay(toSafeDate(val));
  return setSeconds(setMinutes(setHours(date, hours), minutes), seconds) as T;
};

// eslint-disable-next-line @typescript-eslint/naming-convention
const __DEV__ = process.env.NODE_ENV === 'dev';

/**
 * @link https://github.com/uber/baseweb/blob/main/src/timepicker/timepicker.tsx
 */
const PrivateTimePicker = forwardRef(function PrivateTimePicker<T extends Date>(
  {
    ..._props
  }: TimePickerProps<T> &
    Omit<ComponentPropsWithoutRef<typeof Select>, 'onChange' | 'value'>,
  ref: React.ForwardedRef<HTMLDivElement>,
) {
  const buildSteps = useEffectEvent(() => {
    const { step = 900 } = props;

    const timeWindow = getTimeWindowInSeconds(step);
    let stepCount = (timeWindow.end - timeWindow.start) / step;
    if (__DEV__ && stepCount > 500) {
      // eslint-disable-next-line no-console
      console.warn(
        `Provided step value (${step}) results in ${stepCount} steps. Performance may suffer when more than 500 elements are rendered.`,
      );
    }

    if (!Number.isInteger(stepCount)) {
      const previousStepCount = stepCount;
      stepCount = Math.round(stepCount);

      if (__DEV__) {
        // eslint-disable-next-line no-console
        console.warn(
          `Provided step value (${step}) does not spread evenly across a day. Rounding from ${previousStepCount} total steps to ${stepCount}.`,
        );
      }
    }

    const options: number[] = [];
    for (let i = timeWindow.start; i < timeWindow.end; i += step) {
      options.push(i);
    }
    const disabledSlot = props.disabledSlot;
    if (disabledSlot) {
      return options.filter((option) => {
        if (Array.isArray(disabledSlot)) {
          return !disabledSlot.includes(option);
        } else if (typeof disabledSlot === 'function') {
          return !disabledSlot(option);
        }
        return true;
      });
    }
    return options;
  });

  const handleChange = useEffectEvent((seconds: number) => {
    const [hours, minutes] = secondsToHourMinute(seconds);
    const updatedDate = setTime(props.value, hours, minutes, 0);
    if (props.onChange) {
      props.onChange(updatedDate);
    }
  });

  const props = mergeProps(
    {
      format: '12',
      step: 900,
      ignoreMinMaxDateComponent: false,
      nullable: true,
      disabledSlot: [],
    } as TimePickerProps<T>,
    _props,
  );

  const [state, setOptions] = useState<TimePickerState>({
    steps: buildSteps(),
    value: props.value ? buildSelectedOption(props.value, props.format) : null,
  });
  const steps = state.steps ?? [];

  const isMounted = useRef(false);
  useEffect(() => {
    if (isMounted.current) return;
    isMounted.current = true;

    const newSteps = buildSteps();

    if (props.value && isValid(props.value)) {
      setOptions({
        steps: newSteps,
        value: buildSelectedOption(props.value, props.format),
      });
    } else {
      const seconds = dateToSeconds(toSafeDate());
      let closestStep = NOON;
      newSteps.forEach((step) => {
        if (Math.abs(step - seconds) < Math.abs(closestStep - seconds)) {
          closestStep = step;
        }
      });
      setOptions({
        steps: newSteps,
        value: props.nullable
          ? undefined
          : {
              id: String(closestStep),
              label: secondsToLabel(closestStep, props.format),
            },
      });
      if (props.value || (!props.nullable && !props.value)) {
        handleChange(closestStep);
      }
    }
  }, [buildSteps, handleChange, props.format, props.nullable, props.value]);

  const [prevProps, setPrevProps] = useState(props);
  if (JSON.stringify(prevProps) !== JSON.stringify(props)) {
    setPrevProps(props);
  }

  useEffect(() => {
    const formatChanged = prevProps.format !== props.format;
    const stepChanged = prevProps.step !== props.step;
    const disabledSlotChanged =
      JSON.stringify(prevProps.disabledSlot) !==
      JSON.stringify(props.disabledSlot);
    const minTimeChange = prevProps.minTime !== props.minTime;
    const maxTimeChange = prevProps.maxTime !== props.maxTime;

    if (
      formatChanged ||
      stepChanged ||
      minTimeChange ||
      maxTimeChange ||
      disabledSlotChanged
    ) {
      // const newSteps = buildSteps();
      // setOptions((prev) => ({ ...prev, steps: newSteps }));
    }

    const newSteps = buildSteps();
    setOptions((prev) => ({
      ...prev,
      steps: newSteps,
      value: !props.value ? null : prev.value,
    }));
  }, [
    buildSteps,
    prevProps,
    props.format,
    props.maxTime,
    props.minTime,
    props.step,
    props.value,
    props.disabledSlot,
  ]);

  const getTimeWindowInSeconds = (
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _step: number,
  ): {
    start: number;
    end: number;
  } => {
    // eslint-disable-next-line prefer-const
    let { minTime: min, maxTime: max, ignoreMinMaxDateComponent } = props;
    const dayStart = setTime(props.value, 0, 0, 0);
    const dayEnd = setTime(props.value, 24, 0, 0);

    if (!min || (isBefore(min, dayStart) && !ignoreMinMaxDateComponent)) {
      min = dayStart;
    } else {
      min = setTime(
        props.value,
        getHours(min),
        getMinutes(min),
        getSeconds(min),
      );
    }

    if (!max || (isAfter(max, dayEnd) && !ignoreMinMaxDateComponent)) {
      max = dayEnd;
    } else {
      max = setTime(
        props.value,
        getHours(max),
        getMinutes(max),
        // maxTime (if provided) should be inclusive, so add an extra second here
        getSeconds(max) + 1,
      );
    }

    const minDate = toDate(min);
    const maxDate = toDate(max);
    const midnightDate = toDate(dayStart);
    return {
      // @ts-expect-error todo(flow->ts)
      start: (minDate - midnightDate) / 1000,
      // @ts-expect-error todo(flow->ts)
      end: (maxDate - midnightDate) / 1000,
    };
  };

  const onChange = (_e: unknown, value: string | null) => {
    setOptions((prev) => ({
      ...prev,
      ...{
        value: value
          ? {
              id: value.toString(),
              label: secondsToLabel(Number(value), props.format),
            }
          : null,
      },
    }));
    if (!value) {
      if (props.nullable && props.onChange) {
        props.onChange(null);
      }
      return;
    }

    const seconds: number =
      typeof value === 'string' ? parseInt(value, 10) : value || 0;
    handleChange(seconds);
  };

  const value =
    props.value && isValid(props.value)
      ? buildSelectedOption(props.value, props.format)
      : state.value;
  const options = steps.map((n) => ({
    id: n,
    label: secondsToLabel(n, props.format),
  }));

  const containerRef = useRef<HTMLDivElement>(null);
  const [, domProps] = createSplitProps<TimePickerProps<T>>()(props, [
    'format',
    'onChange',
    'step',
    'value',
    'nullable',
    'minTime',
    'maxTime',
    'ignoreMinMaxDateComponent',
    'disabledSlot',
  ]);

  return (
    <Select
      ref={ref}
      {...domProps}
      value={value ? Number(value.id) : null}
      onChange={(_e, newValue) => {
        const isNumber = z.coerce.number().safeParse(newValue).success;
        if (isNumber) {
          onChange(_e, String(newValue));
        }
      }}
      onBlur={(event) => {
        const element = event.relatedTarget;
        if (
          element &&
          (element.tagName === 'A' ||
            element.tagName === 'BUTTON' ||
            element.tagName === 'INPUT')
        ) {
          (element as HTMLElement).focus();
          domProps.onBlur?.(event);
        }
      }}
      slotProps={{
        root: { ref: containerRef },
        listbox: {
          popperOptions: { strategy: 'absolute', placement: 'bottom-start' },
          container: () => containerRef.current,
        },
      }}
      controlled={false}
    >
      {options.length === 0 && (
        <Typography level='body-sm' sx={{ textAlign: 'center' }}>
          No Options
        </Typography>
      )}
      {options.map((option) => (
        <SelectOption key={option.id} value={option.id}>
          {option.label}
        </SelectOption>
      ))}
    </Select>
  );
});

export function TimePicker({
  controlled = true,
  ...props
}: ComponentPropsWithoutRef<typeof PrivateTimePicker> & {
  controlled?: boolean;
}) {
  const controller = useFormController();
  const field = controller?.field;

  return (
    <PrivateTimePicker
      {...mergeProps(
        props,
        controlled && field
          ? ({
              ...controller?.field,
            } as Partial<ComponentPropsWithoutRef<typeof PrivateTimePicker>>)
          : {},
      )}
    />
  );
}
